import { get } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import Icon, { IconName } from "src/components/Icon/Icon";

import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import AppBar from "src/components/AppBar/AppBar";
import DisTable from "src/components/TableV2/Table";
import { IRootState } from "src/store/reducers";
import { fetchApplicationsList, removeApplication, uploadAplication } from "src/store/reducers/applications";

import GuardedComponent from "src/components/GuardedComponent/GuardedComponent";
import TextFilter from "src/components/TableV2/TextFilter";
import { IApplication } from "src/store/models/application.model";
import { UserRole } from "src/store/models/user.model";
import { fetchApplicationOptions } from "src/store/reducers/applicationBuilder";
import { openUrl } from "src/util/util.navigation";
import FilterContainer from "../../components/TableV2/FilterContainer";
import StatusFilterTool from "../../components/TableV2/StatusFilter";
import { applicationsPageColumns } from "./constants";
import { UploadModal } from "./UploadModal";

import { Typography } from "@mui/material";
import PromptModal from "src/components/ConfirmationModal/PromptModalV2";
import { IFinalTableContextValue } from "src/components/FinalTable/FinalTable";
import FinalTableSpy from "src/components/FinalTable/FinalTableSpy";
import { useLoading } from "src/hooks/useLoading";
import { hasAnyAuthority } from "src/util/roleAuthorization";
import * as S from "../../constants/StringConstants";
import "./Applications.scss";

const Applications = () => {
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const dispatch = useDispatch();
  const history = useHistory();
  const appListRef = useRef<any>(null);
  const { setIsLoading } = useLoading();
  const { projection, total, listOptions, map, loading } = useSelector((state: IRootState) => {
    return state.applications;
  });

  const [isUploadOpen, setIsUploadOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchApplicationOptions());
  }, []);

  const handleModalDismiss = () => {
    setIsUploadOpen(false);
  };

  const handleModalAccept = async data => {
    setIsLoading(true);
    setUploadError(null);

    try {
      const res: any = await dispatch(
        uploadAplication(data.carrierTypeId, data.branchTypeId, data.statusTypeId, data.file),
      );

      const status = get(res, "value.status");
      const newAppId = get(res, "value.data");

      if (status === 200 && newAppId) {
        handleModalDismiss();
        history.push(`/applications/${newAppId}`);
      } else {
        setUploadError(S.APP_UPLOAD_MODAL_FILE_ERROR);
      }
    } catch (error) {
      setUploadError(S.APP_UPLOAD_MODAL_ERROR);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetForm = (props: IFinalTableContextValue<{}>) => {
    props.update({ page: 1, query: {} });
  };

  const handleAppDeleteAsk = (id: string) => {
    setDeleteId(id);
  };

  const handleDelete = async () => {
    setIsLoading(true);
    await dispatch(removeApplication(parseInt(deleteId)));
    setDeleteId(null);
    appListRef.current.update({});
    setIsLoading(false);
  };

  const { carrierOptions, branchOptions, statusOptions, userOptions, productOptions, gmeGsiOptions, auth } =
    useSelector((state: IRootState) => ({
      carrierOptions: state.applicationBuilder.selectCarrierTypeList,
      branchOptions: state.applicationBuilder.selectBranchList,
      statusOptions: state.applicationBuilder.selectStatusList,
      userOptions: state.applicationBuilder.selectUWList,
      productOptions: state.applicationBuilder.selectProductList,
      gmeGsiOptions: state.applicationBuilder.selectGmeGsiList,
      auth: state.auth,
    }));

  const organizationId = hasAnyAuthority(auth.user, [UserRole.BranchManager]) ? String(auth.user.branchTypeID) : null;

  return (
    <div className="table-view-v2__content">
      <div className="table-view__header-buttons-container">
        <AppBar icon={IconName.Applications} title={S.APP_PAGE_TITLE} loading={loading}>
          <div className="table-view__buttons-container">
            <GuardedComponent
              authorizedRoles={[
                UserRole.Admin,
                UserRole.ProposalSpecialist,
                UserRole.RegionalBranchManager,
                UserRole.BranchManager,
                UserRole.UnderwritingAssociate,
              ]}>
              <button className="side-nav__add-button" onClick={() => setIsUploadOpen(true)}>
                <Icon name={IconName.Plus} />
                {S.NEW_APPLICATION_UPLOAD}
              </button>
              <button className="side-nav__add-button" onClick={() => openUrl("/applications/new", false)}>
                <Icon name={IconName.Plus} />
                {S.NEW_APPLICATION}
              </button>
            </GuardedComponent>
          </div>
        </AppBar>
      </div>

      <DisTable<IApplication>
        columns={applicationsPageColumns(handleAppDeleteAsk)}
        initialState={{
          rows: 25,
          projection,
          map,
          total,
          ...listOptions,
        }}
        getRef={ft => {
          appListRef.current = ft;
        }}
        onRowClick={(data: IApplication) => {
          history.push(`/applications/${data.id}`);
        }}
        onUpdate={async ({ page, rows, sort, query }) => {
          const result: any = await dispatch(
            fetchApplicationsList({
              page,
              rows,
              sort,
              query: { organizationId, sortOrder: "desc", ...query },
            }),
          );
          return {
            total: get(result, "value.data.totalRows") as number,
            data: get(result, "value.data.items") as IApplication[],
          };
        }}>
        <div className="applications-custom-textfilter">
          <FilterContainer
            left={
              <>
                <strong>{S.APP_FILTERS}</strong>
                <TextFilter
                  fields={[
                    { type: "text", name: "policyNumber", label: S.APP_FILTERS_POLICY_NUMBER },
                    { type: "text", name: "firstName", label: S.APP_FILTERS_FIRST_NAME },
                    { type: "text", name: "lastName", label: S.APP_FILTERS_LAST_NAME },
                    {
                      type: "typeahead",
                      name: "gmegsi",
                      label: S.APP_FILTERS_GME_GSI,
                      options: gmeGsiOptions,
                      paramHandler: value => {
                        if (value) {
                          const arr = value.split(":");
                          return { gmegsiId: arr[1], gmegsiType: arr[0] };
                        }
                        return {};
                      },
                    },
                  ]}
                  inputProps={{ style: { width: "130px", height: "36px" } }}
                />
              </>
            }
            right={
              <>
                <StatusFilterTool
                  key="productID"
                  fieldName="productID"
                  fieldLabel={S.APP_FILTERS_PRODUCT}
                  statusList={productOptions}
                />
                <StatusFilterTool
                  key="statusID"
                  fieldName="statusID"
                  fieldLabel={S.APP_FILTERS_STATUS}
                  statusList={statusOptions}
                />
                <StatusFilterTool
                  fieldName="uwSpecialistId"
                  fieldLabel={S.APP_FILTERS_UW_SPECIALIST}
                  statusList={userOptions}
                />
                {!organizationId && (
                  <StatusFilterTool
                    fieldName="organizationId"
                    fieldLabel={S.APP_FILTERS_BRANCH}
                    statusList={branchOptions}
                  />
                )}
                <StatusFilterTool
                  fieldName="carrierID"
                  fieldLabel={S.APP_FILTERS_CARRIER}
                  statusList={carrierOptions}
                />
                <FinalTableSpy>
                  {props => (
                    <button className="button-v2 gray small" onClick={() => handleResetForm(props)}>
                      {S.APP_FILTERS_RESET}
                    </button>
                  )}
                </FinalTableSpy>
              </>
            }></FilterContainer>
        </div>
      </DisTable>

      {isUploadOpen && (
        <UploadModal handleConfirm={handleModalAccept} handleReject={handleModalDismiss} errorMessage={uploadError} />
      )}

      {deleteId != null && (
        <PromptModal
          open={deleteId != null}
          title={S.APP_DELETE_CONFIRM_TITLE}
          confirmText={S.APP_DELETE_CONFIRM_CONFIRM}
          rejectText={S.APP_DELETE_CONFIRM_REJECT}
          onConfirm={handleDelete}
          onReject={() => setDeleteId(null)}>
          <Typography variant="h6">{S.APP_DELETE_CONFIRM_MESSAGE}</Typography>
        </PromptModal>
      )}
    </div>
  );
};

export default Applications;
